<template>
  <div
    style="position: relative"
    class="filter-item-date d-flex flex-wrap gap-2"
    v-if="operator"
  >
    <div class="d-flex align-items-center">
      <b-form-select
        class="flex-1"
        :modelValue="operator"
        @update:modelValue="updateOperator"
        :options="options"
        size="sm"
      ></b-form-select>
    </div>
    <div
      v-if="operator !== 'isnull' && operator !== 'isnotnull'"
      class="d-flex flex-wrap"
    >
      <div
        class="mt-1"
        v-if="isShowOneDateType"
      >
        <b-form-select
          :modelValue="oneDateType"
          @update:modelValue="updateOneDateType"
          :options="oneDateTypes"
          size="sm"
        ></b-form-select>
      </div>
      <div
        class="mt-1 d-flex align-items-center"
        v-if="isShowRelationType"
      >
        <b-form-input
          type="number"
          size="sm"
          class="mr-1"
          :modelValue="relationPeriodStart"
          @update:modelValue="updateRelationPeriodStart"
          style="width: 50px"
        />
        <b-form-select
          class="mr-1"
          :modelValue="dateRelationPeriodTypeStart"
          @update:modelValue="updateDateRelationPeriodTypeStart"
          :options="dateRelationPeriodTypes"
          size="sm"
          style="width: 60px"
        ></b-form-select>
        <span class="mx-1">〜</span>
        <b-form-input
          type="number"
          size="sm"
          class="mr-1"
          :modelValue="relationPeriodEnd"
          @update:modelValue="updateRelationPeriodEnd"
          style="width: 50px"
        />
        <b-form-select
          :modelValue="dateRelationPeriodTypeEnd"
          @update:modelValue="updateDateRelationPeriodTypeEnd"
          :options="dateRelationPeriodTypes"
          size="sm"
          style="width: 60px"
        ></b-form-select>
      </div>
      <div
        class="filter-calendar"
        v-if="renderCalendar === false && operator !== 'relative'"
      >
        <component
          :is="dateOrTimePicker"
          class="small"
          style="zoom: 0.86"
          :style="{
            minWidth: isRange ? '300px' : '150px',
            maxWidth: isRange ? '400px' : '200px',
          }"
          :enable-seconds="enableSeconds"
          v-if="renderCalendar === false"
          :key="filterItemService.colDef.name + '_datepicker'"
          :modelValue="modelValue"
          @update:modelValue="change"
          :inline="false"
          :disabled="oneDateType !== 'custom_date' || operator === 'relative'"
          :common-attrs="{
            range: isRange,
          }"
          :defaultTime="defaultTime"
        />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { inject } from 'vue'
import { ColumnDef } from '../../../../../../common/$models/ModelDef'
import { FilterItemService } from '../../../FilterItemService'
import {
  DATE_RELATION_PERIOD_TYPES,
  FILTER_OPERATOR_FOR_DATE,
  ONE_DATE_TYPES,
} from '../../../FilterRuleService'

const isShowOneDateTypeKeys = ['eq', 'lt', 'gt', 'lte', 'gte']

export default {
  name: 'FilterItemDate',
  props: {
    modelValue: {
      type: [String, Array] as PropType<string | string[] | null>,
      required: true,
    },
    initialOperator: {
      type: String,
      default: 'between',
    },
  },
  data() {
    return {
      operator: null,
      isVisibleSetting: false,
      oneDateType: null,
      renderCalendar: false,
      dateRelationType: null,
      dateRelationPeriodTypeStart: 'day',
      dateRelationPeriodTypeEnd: 'day',
      relationPeriodStart: -7,
      relationPeriodEnd: 0,
    }
  },
  computed: {
    filterItemService(): FilterItemService<any> {
      // @ts-ignore
      return (
        this.$provides?.filterItemService?.value || this.$provides?.filterObject?.value
      )
    },
    isRange() {
      return (
        this.operator === 'between' ||
        this.operator === 'relative' ||
        this.dateRelationPeriodTypeStart !== 'day' ||
        this.dateRelationPeriodTypeEnd !== 'day'
      )
    },
    defaultTime() {
      if (this.isRange) {
        return ['00:00:00', '23:59:59']
      } else {
        return '00:00:00'
      }
    },
    label() {
      return this.filterItemService.colDef.label || this.filterItemService.colDef.name
    },
    colType(): ColumnDef['type'] {
      return this.filterItemService.colDef.type
    },
    enableSeconds() {
      return this.colType !== 'DATEONLY'
    },
    isShowRelationType() {
      return this.operator === 'relative'
    },
    isShowOneDateType() {
      return isShowOneDateTypeKeys.includes(this.operator)
    },
    dateOrTimePicker() {
      if (this.colType === 'DATEONLY') {
        return 'datepicker'
      } else {
        return 'DatetimePicker'
      }
    },
    options() {
      // { value: xxxx'', text: 'xxxxx' }のような形で返す
      return Object.keys(FILTER_OPERATOR_FOR_DATE).map((operator) => {
        return {
          value: operator,
          text: FILTER_OPERATOR_FOR_DATE[operator],
        }
      })
    },
    oneDateTypes() {
      return Object.keys(ONE_DATE_TYPES).map((type) => {
        return {
          value: type,
          text: ONE_DATE_TYPES[type],
        }
      })
    },
    dateRelationPeriodTypes() {
      return Object.keys(DATE_RELATION_PERIOD_TYPES)
        .filter((key) => key !== 'this')
        .map((type) => {
          return {
            value: type,
            text: DATE_RELATION_PERIOD_TYPES[type],
          }
        })
    },
  },
  mounted() {
    this.$nextTick(() => {
      const isRelativeType = this.modelValue?.[0]?.indexOf?.('$NOW') === 0
      if (isRelativeType) {
        this.operator = 'relative'
        // Value を parse する
        const [start, end] = this.modelValue || []
        if (start) {
          const parsed = $core.$utils.parseRelativeDateFilterValue(start)
          if (parsed) {
            this.relationPeriodStart = parsed.amount
            this.dateRelationPeriodTypeStart = parsed.type
          }
        }
        if (end) {
          const parsed = $core.$utils.parseRelativeDateFilterValue(end)
          if (parsed) {
            this.relationPeriodEnd = parsed.amount
            this.dateRelationPeriodTypeEnd = parsed.type
          }
        }
      } else {
        this.operator =
          this.filterItemService?.value?.operator || this.initialOperator || 'between'
      }
      this.oneDateType = 'custom_date'
    })
  },
  methods: {
    toggleSetting() {
      this.isVisibleSetting = !this.isVisibleSetting
    },
    change(value) {
      if (value && value !== 'Invalid Date') {
        this.$emit('update:modelValue', {
          value: Array.isArray(value)
            ? [
                this.filterItemService.formatDate(value[0]),
                this.filterItemService.formatDate(value[1]),
              ]
            : value,
          valueType: this.oneDateType,
          operator: this.operator,
        })
      }
    },
    updateOneDateType(value) {
      this.oneDateType = value
      if (value !== 'custom_date') {
        this.$emit('update:modelValue', {
          value: this.filterItemService.calcOneDateFromType(value),
          valueType: this.oneDateType,
          operator: this.operator,
        })
      }
    },
    updateOperator(operator) {
      this.operator = operator
      this.oneDateType = 'custom_date'
      this.renderCalendar = true
      if (this.operator === 'between') {
        this.$emit('update:modelValue', {
          value: ['', ''],
          valueType: this.oneDateType,
          operator,
        })
      } else if (this.operator === 'relative') {
        this.updatePropertyModelValue()
      } else if (this.operator === 'isnull' || operator === 'isnotnull') {
        this.$emit('update:modelValue', {
          value: '',
          valueType: this.oneDateType,
          operator,
        })
      } else {
        this.$emit('update:modelValue', {
          value: Array.isArray(this.modelValue) ? '' : this.modelValue,
          valueType: this.oneDateType,
          operator,
        })
      }
      setTimeout(() => {
        this.renderCalendar = false
      }, 1)
    },
    updateRelationPeriodStart(newValue) {
      this.relationPeriodStart = newValue
      this.updatePropertyModelValue()
    },
    updateRelationPeriodEnd(newValue) {
      this.relationPeriodEnd = newValue
      this.updatePropertyModelValue()
    },
    updateDateRelationPeriodTypeStart(newValue) {
      this.dateRelationPeriodTypeStart = newValue
      this.updatePropertyModelValue()
    },
    updateDateRelationPeriodTypeEnd(newValue) {
      this.dateRelationPeriodTypeEnd = newValue
      this.updatePropertyModelValue()
    },
    updatePropertyModelValue() {
      this.$emit('update:modelValue', {
        value: this.filterItemService.formatDateFromRelation(
          'relative',
          this.dateRelationPeriodTypeStart,
          this.relationPeriodStart,
          this.dateRelationPeriodTypeEnd,
          this.relationPeriodEnd,
        ),
        valueType: this.oneDateType,
        operator: this.operator === 'relative' ? 'between' : this.operator,
      })
    },
    onDismissDropDown() {
      this.$emit('onDismissDropdown')
    },
  },
}
</script>
